/* eslint-disable max-lines */
import { action, computed, observable } from 'mobx';
import { Store } from 'bernie-plugin-mobx';

import { ClientLogger } from 'bernie-client';
import { getErrorString, NOOP_LOGGER } from 'bernie-logger';
import { Localization } from 'bernie-l10n';
import { merge, SerializedData } from 'bernie-core';

import { ILocalization } from '@shared-ui/localization-context';

import { STORE_EVENTS } from '../events/store-events';

import { HOUSE_RULES_GROUP } from '../../constants/filters';
import { PaginationSubSets } from 'models';

import { SearchCriteriaUtils } from './search-criteria-utils';

import { CleaningAndSafetyPractice, PaymentType, PropertyFilterAmenity } from '__generated__/api';
import { RoomModel } from './room-model';

const { withoutKeys } = merge;

const logger = ClientLogger.getLoggerWithIdentifier('pwa.store.searchCriteriaModel');

/**
 * We'll soon phase out the bex-api-lodging definition of search criteria,
 * so we need this separate definition.
 */
export enum PropertySort {
  AARP_MEMBER_DISCOUNT = 'AARP_MEMBER_DISCOUNT',
  BEST_DEAL = 'BEST_DEAL',
  DISTANCE = 'DISTANCE',
  MEMBER_PRICING = 'MEMBER_PRICING',
  PRICE_HIGH_TO_LOW = 'PRICE_HIGH_TO_LOW',
  PRICE_LOW_TO_HIGH = 'PRICE_LOW_TO_HIGH',
  PRICE_RELEVANT = 'PRICE_RELEVANT',
  PROPERTY_CLASS = 'PROPERTY_CLASS',
  PROPERTY_NAME = 'PROPERTY_NAME',
  RECOMMENDED = 'RECOMMENDED',
  REVIEW = 'REVIEW',
  REVIEW_COUNT = 'REVIEW_COUNT',
  VACATION_RENTAL = 'VACATION_RENTAL',
}

interface SearchCriteriaDateProps extends SerializedData {
  startDate: string;
  endDate: string;
}
const hasDates = (state: SerializedData): state is SearchCriteriaDateProps =>
  'startDate' in state && 'endDate' in state && typeof state.startDate === 'string' && typeof state.endDate === 'string';

interface SearchCriteriaModelProps extends SerializedData {
  rooms: RoomModel[];
}

const hasRooms = (state: SerializedData): state is SearchCriteriaModelProps => 'rooms' in state;

export class SearchCriteriaModel extends Store {
  [key: string]: any; // index signature for type safety
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4;
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

  public static getDefaults() {
    return {
      destination: '',
      endDate: null,
      group: '',
      latLong: '',
      regionId: '',
      selected: '',
      startDate: null,
      userIntent: '',
      neighborhood: '',
      sort: PropertySort.RECOMMENDED,
    };
  }

  constructor(state: SerializedData = {}, l10n?: ILocalization) {
    super({}, NOOP_LOGGER);

    this.l10n = l10n || new Localization('en_US');
    this.localDateFormat = state.localDateFormat || 'yyyy-MM-dd';

    this.initializeDateProperties(state);

    Object.assign(this, SearchCriteriaModel.acceptedKeys(state));
    this.setRooms(state);
  }

  private initializeDateProperties(state: SerializedData) {
    // These properties are initialized as enumerable as part of the work to replace mobx interceptor.
    // This allows them to use custom getters and setters without making the fields private
    Object.defineProperty(this, 'startDate', {
      enumerable: true,
      get() {
        return this._startDate;
      },
      set(startDate?: string) {
        this._startDate = this.sanitizeDate(startDate);
      },
    });

    Object.defineProperty(this, 'endDate', {
      enumerable: true,
      get() {
        return this._endDate;
      },
      set(startDate?: string) {
        this._endDate = this.sanitizeDate(startDate);
      },
    });
    this.setDates(state);
  }

  private setDates(state: SerializedData) {
    if (hasDates(state)) {
      this.startDate = state.startDate;
      this.endDate = state.endDate;

      return;
    }
    this.startDate = undefined;
    this.endDate = undefined;
  }

  // keep dates in the data format that we want, in this case ISO, and the UI can localize based on this date
  public sanitizeDate(date?: string) {
    if (!date) return undefined;
    try {
      date = decodeURIComponent(date);
      let dateObj: Date = this.l10n.parseLocalDateString(date, { raw: this.localDateFormat }) as Date;

      if (!dateObj) {
        dateObj = this.l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
      }

      const cleanDate = this.l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

      if (cleanDate) {
        return cleanDate;
      }

      throw new Error(`Invalid Date: cannot parse ${date} into proper date`);
    } catch (error) {
      if (logger) {
        logger.logEvent(STORE_EVENTS.MOBX_INTERCEPTOR_ERROR, getErrorString(error as Error));
      }

      return undefined;
    }
  }

  public hydrate(data: SerializedData) {
    if (typeof data === 'object' && data !== null) {
      Object.assign(this, SearchCriteriaModel.acceptedKeys(data));
      this.setRooms(data);
    }
  }

  // Should not use `null` as a default; may deserialize as empty string.
  public static DEFAULTS: SerializedData = {
    accessibility: [],
    agencyBusinessModels: [],
    amenities: [],
    bedroomFilter: [],
    chain: '',
    commissionTiers: [],
    daysInFuture: '',
    deals: [],
    destination: '',
    group: '',
    guestRating: '',
    hotelName: '',
    latLong: '',
    lodging: [],
    mealPlan: [],
    misId: '',
    dropOffTime: '',
    pickUpTime: '',
    airlineCode: '',
    cabinClass: '',
    directFlights: '',
    driverAge: '',
    tripType: '',
    origin: '',
    infantsInSeats: '',
    paymentType: [],
    bedType: [],
    cleaningAndSafetyPractices: [],
    poi: '',
    price: [],
    propertyStyle: [],
    regionId: '',
    neighborhood: '',
    rewards: [],
    roomIndex: '',
    rooms: [],
    selected: '',
    sort: PropertySort.RECOMMENDED,
    star: [],
    stayLength: '',
    theme: '',
    travelerType: [],
    useRewards: '',
    userIntent: '',
    partialStay: 'false',
    mapBounds: [],
    stayType: [],
    expediaPropertyId: '',
    // eslint-disable-next-line camelcase
    house_rules_group: [],
    vacationRentalsOnly: false,
    highlightedPropertyId: '',
    dateSelectedByUser: false,
  };

  @observable
  public l10n: ILocalization;

  @observable
  public adultsString?: string;
  @observable
  public amenities: PropertyFilterAmenity[] = [];
  @observable
  public chain?: string;
  @observable
  public childrenString?: string;
  @observable
  public daysInFuture?: string;
  @observable
  public origin?: string;
  @observable
  public destination = '';
  @observable
  private _endDate?: string;
  public endDate!: any;

  @observable
  public group?: string;
  @observable
  public guestRating?: string;
  @observable
  public hotelName?: string;
  @observable
  public latLong?: string;
  @observable
  public localDateFormat: string;
  @observable
  public lodging?: any;
  @observable
  public paymentType?: PaymentType[];
  @observable
  public bedType?: string[];
  @observable
  public cleaningAndSafetyPractices?: CleaningAndSafetyPractice[];
  @observable
  public poi?: string;
  @observable
  public price?: string[];
  @observable
  public regionId?: string;
  @observable
  public neighborhood?: string;
  @observable
  public roomIndex?: string;
  @observable
  public rooms: RoomModel[] = [];
  @observable
  public selected?: string;
  @observable
  public semdtl?: any;
  @observable
  public sort?: PropertySort;
  @observable
  public star?: string[];
  @observable
  private _startDate?: string;
  public startDate!: any;
  @observable
  public stayLength?: string;
  @observable
  public theme?: string;
  @observable
  public travelerType?: [];
  @observable
  public useRewards?: string;
  @observable
  public userIntent?: string;
  @observable
  public bedroomFilter?: string[];
  @observable
  public deals?: [];
  @observable
  public propertyStyle?: [];
  @observable
  public multiProp?: string;
  @observable
  public misId?: string;
  @observable
  public referrerUrl?: string;
  @observable
  public rewards?: string[];
  @observable
  public pickUpTime?: string;
  @observable
  public dropOffTime?: string;
  @observable
  public commissionTiers?: [];
  @observable
  public agencyBusinessModels?: [];
  @observable
  public mealPlan?: [];
  @observable
  public cabinClass?: string;
  @observable
  public tripType?: string;
  @observable
  public airlineCode?: string;
  @observable
  public directFlights?: string;
  @observable
  public infantsInSeats?: string;
  @observable
  public driverAge?: string;
  @observable
  public paandi?: string;
  @observable
  public partialStay?: string;
  @observable
  public partialStayStartDate?: string;
  @observable
  public partialStayEndDate?: string;
  @observable
  public packageRates?: string;
  @observable
  public vacationRentalsOnly?: boolean;
  @observable
  public mapBounds: any;
  @observable
  public stayType?: [];
  @observable
  public expediaPropertyId?: string;
  @observable
  public typeaheadRegionType?: number;
  @observable
  // eslint-disable-next-line camelcase
  public house_rules_group: HOUSE_RULES_GROUP[] = [];
  @observable
  public highlightedPropertyId?: string;
  @observable
  public flexibility?: string | string[];
  @observable
  public paginationSubSets!: PaginationSubSets;
  @observable
  public dateSelectedByUser?: boolean;
  @observable
  public searchId?: string;

  @computed
  public get isPartialStay(): boolean {
    return this.partialStay === 'true';
  }

  @computed
  public get adults(): number {
    if (this.rooms && this.rooms.length > 0) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      return this.rooms.reduce((base, room) => base + (room.adults || 0), 0);
    }

    return 0;
  }

  @computed
  public get children(): number {
    if (this.rooms && this.rooms.length > 0) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      return this.rooms.reduce((base, room) => base + ((room.children && room.children.length) || 0), 0);
    }

    return 0;
  }

  @computed
  get guestCount(): number {
    return this.adults + this.children;
  }

  @computed
  get isDateless(): boolean {
    return !this.startDate && !this.endDate;
  }

  public clone(): SearchCriteriaModel {
    return new SearchCriteriaModel(this.toJSON(), this.l10n);
  }

  private setRooms(state: SerializedData = {}) {
    if (hasRooms(state) && state.rooms?.length) {
      this.rooms = state.rooms.map(room => new RoomModel({ ...room }));

      return;
    }
    this.rooms = [new RoomModel({ ...RoomModel.DEFAULTS })];
  }

  private static acceptedKeys(data: any = {}): any {
    return withoutKeys(['adults', 'children', 'dateless', 'rooms']).merge({}, data);
  }

  public toJSON(ignoreAdditionalKeys?: string[] | string): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys && ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }
    const json = super.toJSON(ignoreAdditionalKeys.concat('rooms', 'l10n'));

    json.rooms = (this.rooms && this.rooms.map(room => room.toJSON())) || undefined;

    return json;
  }

  @action
  public setValues(query: SerializedData = {}): void {
    if (!query.adults) {
      query.adults = '2';
    }

    if (!query.children || query.children === '0' || query.children === 0) {
      query.children = '';
    }

    query = SearchCriteriaUtils.ensureCorrectTypes(query);

    query.adultsString = query.adults;
    query.childrenString = query.children;

    if (query.startDate === '' && query.endDate === '') {
      query.startDate = null;
      query.endDate = null;
    }

    if (!query.flexibility) {
      query.flexibility = null;
    }

    Object.assign(this, SearchCriteriaModel.acceptedKeys(query));
  }

  @action
  public setHighlightedPropertyId(id: string) {
    this.highlightedPropertyId = id;
  }

  @computed
  get getHighlightedPropertyId(): string | undefined {
    return this.highlightedPropertyId === '' ? undefined : this.highlightedPropertyId;
  }
}
